import React, { useRef } from 'react';
import { motion, useScroll, useTransform, useSpring, useMotionValue } from 'framer-motion';
import { ArrowRight, Leaf, Sparkles } from 'lucide-react';
import { HERO_IMAGE } from '@/lib/data';
import { MaskedLines, FadeIn } from './motion/Reveal';
import { TID } from '@/constants/testIds';

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start start', 'end start'],
  });

  // Scroll-linked parallax
  const yImage = useTransform(scrollYProgress, [0, 1], [0, -110]);
  const yBlobA = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const yBlobB = useTransform(scrollYProgress, [0, 1], [0, -140]);
  const scaleImage = useTransform(scrollYProgress, [0, 1], [1, 1.08]);

  // Mouse parallax on image
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 120, damping: 20 });
  const sy = useSpring(my, { stiffness: 120, damping: 20 });

  const onMouseMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const nx = (e.clientX - rect.left - rect.width / 2) / rect.width;
    const ny = (e.clientY - rect.top - rect.height / 2) / rect.height;
    mx.set(nx * 16);
    my.set(ny * 16);
  };

  return (
    <section
      id="top"
      ref={ref}
      data-testid={TID.heroSection}
      className="relative pt-28 pb-16 md:pt-36 md:pb-24 overflow-hidden cw-grain"
    >
      {/* Kinetic background blobs */}
      <motion.div
        aria-hidden
        style={{ y: yBlobA }}
        className="absolute -top-20 -left-10 w-[28rem] h-[28rem] rounded-full bg-[var(--cw-yellow)] blur-3xl opacity-70 pointer-events-none"
      />
      <motion.div
        aria-hidden
        style={{ y: yBlobB }}
        className="absolute bottom-0 -right-20 w-[32rem] h-[32rem] rounded-full bg-[var(--cw-blue)] blur-3xl opacity-60 pointer-events-none"
      />

      <div className="cw-container grid md:grid-cols-12 gap-12 items-center relative">
        <div className="md:col-span-6">
          <FadeIn delay={0.05}>
            <span className="cw-label">A small batch studio · Nagpur, India · est. 2026</span>
          </FadeIn>

          <MaskedLines
            className="font-heading mt-5 text-5xl sm:text-6xl lg:text-[5.5rem] font-medium tracking-tight leading-[1.02] text-[var(--cw-text)]"
            delayStart={0.2}
            lines={[
              <>
                Soap to <span className="italic font-light">cleanse</span>.
              </>,
              <>
                Chocolate to{' '}
                <span className="italic font-light text-[var(--cw-blue-hover)]">indulge</span>.
              </>,
            ]}
          />

          <FadeIn delay={0.55}>
            <p className="mt-6 text-base sm:text-lg leading-relaxed text-[var(--cw-muted)] max-w-xl">
              CHOCO-WASH crafts five quietly elegant soaps and five single-origin
              chocolate bars — packed alone, as a family pack, or wrapped into a
              gift hamper that feels like a soft, sunny morning.
            </p>
          </FadeIn>

          <FadeIn delay={0.7}>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <a href="#soaps" className="cw-btn-primary" data-testid={TID.heroShopBtn}>
                Shop the collection <ArrowRight className="w-4 h-4" />
              </a>
              <a
                href="#story"
                className="cw-btn-ghost text-sm border border-[var(--cw-border)] hover:bg-white"
                data-testid={TID.heroStoryBtn}
              >
                Our story
              </a>
            </div>
          </FadeIn>

          <FadeIn delay={0.85}>
            <div className="mt-12 flex flex-wrap gap-6 text-sm text-[var(--cw-muted)]">
              <div className="flex items-center gap-2">
                <Leaf className="w-4 h-4 text-[var(--cw-blue-hover)]" />
                Plant-based · cold-process
              </div>
              <div className="flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-[var(--cw-blue-hover)]" />
                Single-origin cacao
              </div>
            </div>
          </FadeIn>
        </div>

        <div className="md:col-span-6" onMouseMove={onMouseMove}>
          <motion.div
            className="relative"
            style={{ y: yImage, scale: scaleImage }}
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.1, delay: 0.25, ease: [0.22, 1, 0.36, 1] }}
          >
            {/* Spotlight halo */}
            <div
              aria-hidden
              className="absolute inset-0 -m-10 rounded-full pointer-events-none"
              style={{
                background:
                  'radial-gradient(45% 45% at 50% 50%, rgba(255, 245, 200, 0.7), transparent 70%)',
              }}
            />

            {/* Framed clipped hero image */}
            <motion.div
              style={{ x: sx, y: sy }}
              className="relative rounded-[2.5rem] overflow-hidden cw-soft-shadow border border-white/60"
            >
              <img
                src={HERO_IMAGE}
                alt="Soap and chocolate on a pedestal"
                className="w-full h-[540px] object-cover"
              />
              {/* Vertical crop-line rules */}
              <div className="absolute inset-y-8 left-8 w-px bg-white/30" />
              <div className="absolute inset-y-8 right-8 w-px bg-white/30" />
            </motion.div>

            {/* Floating card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.9, duration: 0.7 }}
              style={{ y: sy, x: sx }}
              className="absolute bottom-6 left-6 bg-white/85 backdrop-blur-md rounded-2xl px-5 py-4 shadow-md border border-white/60"
            >
              <div className="cw-label">This week</div>
              <div className="font-heading text-lg font-medium mt-1">
                Free shipping over ₹500
              </div>
            </motion.div>

            {/* Small badge — top */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 1.05, duration: 0.6, ease: 'backOut' }}
              className="absolute -top-4 -right-4 w-24 h-24 rounded-full bg-[var(--cw-text)] text-white flex flex-col items-center justify-center text-[10px] uppercase tracking-[0.2em] font-semibold rotate-[8deg] shadow-lg"
            >
              <div>Small</div>
              <div className="text-[8px] mt-1">·  batch  ·</div>
              <div>2026</div>
            </motion.div>
          </motion.div>
        </div>
      </div>

      {/* Scroll indicator */}
      <motion.div
        aria-hidden
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4, duration: 0.8 }}
        className="hidden md:flex absolute bottom-6 left-1/2 -translate-x-1/2 flex-col items-center gap-2 text-[10px] uppercase tracking-[0.28em] text-[var(--cw-muted)]"
      >
        Scroll
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 1.8, repeat: Infinity, ease: 'easeInOut' }}
          className="w-px h-8 bg-[var(--cw-muted)]"
        />
      </motion.div>
    </section>
  );
};
